import { Text } from "@react-three/drei";
import { useEffect, useState } from "react";
import { useYjsAwareness } from "../state/yjs-provider";

/** Remote cursor data */
interface RemoteCursor {
  user: { name: string; color: string };
  cursor: { x: number; z: number } | null;
}

/**
 * Component that renders remote user cursors on the scene.
 * Subscribes to Yjs Awareness to track and display other users' cursor positions.
 */
export function RemoteCursors() {
  const awareness = useYjsAwareness();
  const [cursors, setCursors] = useState<Map<number, RemoteCursor>>(new Map());

  useEffect(() => {
    if (!awareness) return;

    const updateCursors = () => {
      const states = awareness.getStates();
      const newCursors = new Map<number, RemoteCursor>();

      states.forEach((state: any, clientId: number) => {
        if (state?.cursor && state.user) {
          // Skip our own cursor (identified by awareness.clientID)
          if (clientId !== awareness.clientID) {
            newCursors.set(clientId, state);
          }
        }
      });

      setCursors(newCursors);
    };

    // Initial update
    updateCursors();

    // Listen for changes
    awareness.on("change", updateCursors);

    return () => {
      awareness.off("change", updateCursors);
    };
  }, [awareness]);

  return (
    <>
      {Array.from(cursors.entries()).map(([clientId, cursor]) => {
        if (!cursor.cursor) return null;

        const position: [number, number, number] = [
          cursor.cursor.x,
          0.05,
          cursor.cursor.z,
        ];

        return (
          <group key={clientId} position={position}>
            {/* Cursor dot */}
            <mesh rotation={[-Math.PI / 2, 0, 0]}>
              <circleGeometry args={[0.15, 16]} />
              <meshBasicMaterial
                color={cursor.user.color}
                transparent
                opacity={0.8}
              />
            </mesh>

            {/* User name label */}
            <Text
              position={[0, 0.3, 0]}
              fontSize={0.2}
              color={cursor.user.color}
              anchorX="center"
              anchorY="bottom"
            >
              {cursor.user.name}
            </Text>
          </group>
        );
      })}
    </>
  );
}
